// src/pages/BulkOrderUpload.js
import React, { useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  deleteDoc,
  doc,
} from "firebase/firestore";

import Papa from "papaparse";
import Layout from "../components/Layout";

export default function BulkOrderUpload() {
  const [loading, setLoading] = useState(false);
  const [previewRows, setPreviewRows] = useState(null);
  const [totalBatchQty, setTotalBatchQty] = useState(0);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const data = results.data;
          const headers = results.meta.fields.map((h) => h.trim());

          const partyCol = headers.find((h) => {
            const low = h.toLowerCase();
            return low === "party name" || low === "party" || low === "partyname";
          });

          if (!partyCol) {
            alert('Required column "Party Name" not found in CSV headers.');
            setLoading(false);
            return;
          }

          const skuHeaders = headers.filter((h) => h !== partyCol);

          const skuSnap = await getDocs(collection(db, "skuMaster"));
          const skuMap = {};
          skuSnap.forEach((d) => {
            const data = d.data();
            skuMap[data.sku] = data;
          });

          const partySnap = await getDocs(collection(db, "partyMaster"));
          const partyNameMap = {};
          const partyClientMap = {};
          const partyPaymentModeMap = {};
          partySnap.forEach((d) => {
            const data = d.data();
            const pName = (data.name || "").trim();
            partyNameMap[pName] = data.partyId;
            partyClientMap[pName] = data.clientId;
            partyPaymentModeMap[pName] = data.payment_mode || "Cash";
          });

          const invalidSkus = skuHeaders.filter((s) => s && !skuMap[s]);
          if (invalidSkus.length > 0) {
            alert("Invalid SKU columns: " + invalidSkus.join(", "));
            setLoading(false);
            return;
          }

          let rows = [];
          let grandTotal = 0;

          for (let row of data) {
            const pName = (row[partyCol] || "").trim();
            if (!pName || !partyNameMap[pName]) continue;

            const items = {};
            let tQty = 0;
            let tAmt = 0;

            for (let skuCode of skuHeaders) {
              const qty = Number(row[skuCode] || 0);
              if (!qty) continue;

              const m = skuMap[skuCode];
              const rate = Number(m.Basic_Eway || 0);
              const basic = qty * rate;
              const gst = basic * (Number(m.gst || 0) / 100);
              const cess = basic * (Number(m.cess || 0) / 100);
              const addl = qty * Number(m.addlcess || 0);
              const rebate = qty * Number(m.Rebate || 0);

              items[skuCode] = qty;
              tQty += qty;
              tAmt += basic + gst + cess + addl + rebate;
            }

            if (tQty > 0) {
              rows.push({
                partyName: pName,
                partyId: partyNameMap[pName],
                clientId: partyClientMap[pName],
                paymentMode: partyPaymentModeMap[pName] || "Cash",
                items,
                totalQty: tQty,
                totalAmount: tAmt,
              });
              grandTotal += tQty;
            }
          }

          setPreviewRows(rows);
          setTotalBatchQty(grandTotal);
        } catch (err) {
          console.error(err);
          alert("Error parsing CSV: " + err.message);
        } finally {
          setLoading(false);
        }
      },
      error: (err) => {
        alert("Error parsing CSV: " + err.message);
        setLoading(false);
      },
    });
  };

  const handleApprove = async () => {
    if (!previewRows || previewRows.length === 0) return;
    const ok = window.confirm(
      `Are you sure you want to UPLOAD ${previewRows.length} orders?\nTotal Quantity: ${totalBatchQty}\n\nNote: This will ONLY replace pending orders for the parties listed below. Orders for other parties will NOT be touched.`
    );
    if (!ok) return;

    setLoading(true);
    try {
      // 1. DELETE OLD PENDING ONLY FOR PARTIES IN THIS BATCH
      const partyIdsInBatch = previewRows.map(r => r.partyId);
      const oldSnap = await getDocs(
        query(collection(db, "orders"), where("status", "==", "PENDING"))
      );
      
      for (let d of oldSnap.docs) {
        const orderData = d.data();
        if (partyIdsInBatch.includes(orderData.partyId)) {
          await deleteDoc(doc(db, "orders", d.id));
        }
      }

      // 2. CREATE NEW ORDERS
      for (let row of previewRows) {
        await addDoc(collection(db, "orders"), {
          clientId: row.clientId,
          partyId: row.partyId,
          partyName: row.partyName,
          items: row.items,
          totalQty: row.totalQty,
          totalAmount: row.totalAmount,
          status: "PENDING",
          payment_mode: row.paymentMode || "Cash",
          source: "BULK_UPLOAD_PREVIEW",
          createdAt: serverTimestamp(),
        });
      }

      alert("Orders uploaded successfully!");
      setPreviewRows(null);
      setTotalBatchQty(0);
    } catch (err) {
      console.error(err);
      alert("Error finalizing upload: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadSample = async () => {
    const skuSnap = await getDocs(collection(db, "skuMaster"));
    const skuList = skuSnap.docs
      .map((d) => d.data())
      .sort((a, b) => (Number(a.sortorder) || 0) - (Number(b.sortorder) || 0));

    const skus = skuList.map((s) => s.sku);

    if (skus.length === 0) {
      alert("No SKUs found in master to create template.");
      return;
    }

    const headers = ["Party Name", ...skus].join(",");

    // 🔹 LOAD ALL PARTIES FOR TEMPLATE
    const partySnap = await getDocs(collection(db, "partyMaster"));
    const partyNames = partySnap.docs
      .map((d) => (d.data().name || "").trim())
      .filter(Boolean)
      .sort();

    const rows = partyNames.length > 0 
      ? partyNames.map(name => [name, ...skus.map(() => "0")].join(","))
      : [["Sample Party Name", ...skus.map(() => "0")].join(",")];

    const csvContent = headers + "\n" + rows.join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "bulk_order_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <div style={{ padding: "40px 20px", background: "#f8f9fb", minHeight: "100vh" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          
          <div style={{ marginBottom: 30 }}>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 700, color: "#1a1a1a" }}>
              Bulk Order Management
            </h1>
            <p style={{ marginTop: 8, color: "#666", fontSize: 16 }}>
              Upload and synchronize warehouse orders via CSV batch processing.
            </p>
          </div>

          {!previewRows ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              
              {/* STEP 1: DOWNLOAD */}
              <div style={{
                background: "#fff",
                padding: 32,
                borderRadius: 16,
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
                border: "1px solid #e5e7eb"
              }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
                  <div style={{ width: 32, height: 32, background: "#f3f4f6", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginRight: 12, fontWeight: 700, color: "#374151" }}>1</div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Get Template</h3>
                </div>
                <p style={{ color: "#6b7280", margin: "0 0 24px 0", lineHeight: 1.5 }}>
                  Download the latest SKU & Party template based on your current master data to ensure format compatibility.
                </p>
                <button
                  onClick={downloadSample}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "#4b5563",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontWeight: 600,
                    transition: "all 0.2s"
                  }}
                >
                  Download Master Template
                </button>
              </div>

              {/* STEP 2: UPLOAD */}
              <div style={{
                background: "#fff",
                padding: 32,
                borderRadius: 16,
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
                border: "1px solid #e5e7eb"
              }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
                  <div style={{ width: 32, height: 32, background: "#f3f4f6", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginRight: 12, fontWeight: 700, color: "#374151" }}>2</div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Upload Data</h3>
                </div>
                <p style={{ color: "#6b7280", margin: "0 0 24px 0", lineHeight: 1.5 }}>
                  Select your completed CSV file. A preview will be generated for your final verification before any live changes.
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFile}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px dashed #d1d5db",
                    borderRadius: 8,
                    background: "#f9fafb"
                  }}
                />
                {loading && (
                  <div style={{ marginTop: 16, display: "flex", alignItems: "center", color: "#2563eb", fontWeight: 600 }}>
                    <span style={{ fontSize: 14 }}>Processing CSV Data...</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* PREVIEW STATE */
            <div style={{
              background: "#fff",
              padding: 40,
              borderRadius: 16,
              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Data Verification Preview</h3>
                  <p style={{ color: "#666", marginTop: 8 }}>
                    Please review the order summaries below. This data has not been saved yet.
                  </p>
                </div>
                <div style={{ textAlign: "right", background: "#fdf2f2", padding: "8px 16px", borderRadius: 8, border: "1px solid #fecaca" }}>
                  <span style={{ color: "#b91c1c", fontSize: 13, fontWeight: 600 }}>ℹ️ Smart Sync Notice</span>
                  <div style={{ fontSize: 12, color: "#7f1d1d", marginTop: 2 }}>Approval replaces pending orders ONLY for the parties listed below.</div>
                </div>
              </div>

              <div style={{ border: "1px solid #edf2f7", borderRadius: 12, overflow: "hidden", marginBottom: 32 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f8f9fb", textAlign: "left" }}>
                      <th style={{ padding: "16px 20px", fontSize: 14, color: "#4a5568", borderBottom: "1px solid #edf2f7" }}>Party Name</th>
                      <th style={{ padding: "16px 20px", fontSize: 14, color: "#4a5568", borderBottom: "1px solid #edf2f7", textAlign: "right" }}>Order Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f7fafc" }}>
                        <td style={{ padding: "14px 20px", fontWeight: 500, color: "#2d3748" }}>{r.partyName}</td>
                        <td style={{ padding: "14px 20px", textAlign: "right", fontWeight: 600, color: "#2d3748" }}>
                          {Number(r.totalQty).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: "#f8fafc" }}>
                      <td style={{ padding: "20px", fontWeight: 700, color: "#1a202c", fontSize: 16 }}>GRAND TOTAL (BATCH)</td>
                      <td style={{ padding: "20px", textAlign: "right", fontWeight: 800, color: "#166534", fontSize: 18 }}>
                        {Number(totalBatchQty).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", gap: 16 }}>
                <button
                  onClick={handleApprove}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: "16px",
                    background: "#166534",
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 700,
                    fontSize: 16,
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)"
                  }}
                >
                  {loading ? "Synchronizing..." : `Approve & Initialize ${previewRows.length} Orders`}
                </button>
                <button
                  onClick={() => setPreviewRows(null)}
                  disabled={loading}
                  style={{
                    padding: "16px 32px",
                    background: "#fff",
                    color: "#4b5563",
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 600
                  }}
                >
                  Discard Changes
                </button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 40, padding: 24, background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb" }}>
            <h4 style={{ margin: "0 0 12px 0", fontSize: 15, fontWeight: 600 }}>System Guidelines</h4>
            <div style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6 }}>
              • Column 1 is strictly reserved for "Party Name" (must match Party Master).<br />
              • All subsequent headers are treated as SKU codes.<br />
              • The upload process is smart: it only replaces 'Pending' orders for the parties found in your CSV.<br />
              • Orders for parties NOT in this CSV will remain untouched in the system.
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
